import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { getMediaType } from '@/lib/media'
import { uploadFileToBlossom, BLOSSOM_SERVERS } from '@/lib/blossom'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { compressImage, isCompressibleImage, formatFileSize, getCompressionStats } from '@/lib/image-compression'

const COMPRESSED_IMAGE_EXTENSIONS: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
}

function isImageUploadDebugEnabled(): boolean {
  return typeof process !== 'undefined' && process.env?.IMAGE_UPLOAD_DEBUG === 'true'
}

function withMimeExtension(fileName: string, mimeType: string): string {
  const extension = COMPRESSED_IMAGE_EXTENSIONS[mimeType.toLowerCase()] ?? '.jpg'
  return fileName.match(/\.[^/.]+$/) ? fileName.replace(/\.[^/.]+$/, extension) : `${fileName}${extension}`
}

interface ImageUploaderProps {
  src: string | null
  index: number
  imagesLength: number
  forSingle?: boolean
  compact?: boolean
  initialUrl?: string
  imageDimensionText?: string
  preferredServer?: string
  onUploadAttempt?: () => boolean
  onBeforeUpload?: () => Promise<void>
  onSave: (data: { url: string; index: number }) => void
  onDelete: (index: number) => void
  onPromote?: (index: number) => void
  onDemote?: (index: number) => void
  onInteraction?: () => void
  onUrlChange?: (url: string) => void
}

export function ImageUploader({
  src,
  index,
  imagesLength,
  forSingle = false,
  compact = false,
  initialUrl = '',
  onSave,
  onDelete,
  onPromote,
  onDemote,
  onInteraction,
  onUrlChange,
  imageDimensionText = "dimensions: 1600px High x 1600px Wide",
  preferredServer,
  onUploadAttempt,
  onBeforeUpload,
}: ImageUploaderProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [urlError, setUrlError] = useState<string | null>(null)
  const [inputEditable, setInputEditable] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [localSrc, setLocalSrc] = useState<string | null>(src)
  const [inputValue, setInputValue] = useState(initialUrl || '')
  const [hasInteracted, setHasInteracted] = useState(false)
  const [selectedServer, setSelectedServer] = useState<string>(BLOSSOM_SERVERS[0].url)
  const [compressionStatus, setCompressionStatus] = useState<string | null>(null)
  const inputTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const compactInputRef = useRef<HTMLInputElement>(null)

  async function performBlossomUpload(file: File) {
    setIsLoading(true)
    const debug = isImageUploadDebugEnabled()

    try {
      await onBeforeUpload?.()

      // Step 1: Compress image if it's compressible
      let fileToUpload = file
      if (isCompressibleImage(file)) {
        try {
          setCompressionStatus(`Compressing image (${formatFileSize(file.size)})...`)
          
          const compressedBlob = await compressImage(file, {
            maxSizeMB: 2,
            quality: 0.7,
            maxWidth: 1600,
            maxHeight: 1600,
            mimeType: undefined, // Let it auto-select optimal format
            debug,
          })

          if (compressedBlob.size < file.size) {
            fileToUpload = new File([compressedBlob], withMimeExtension(file.name, compressedBlob.type), {
              type: compressedBlob.type,
              lastModified: file.lastModified,
            })

            if (debug) {
              const stats = getCompressionStats(file.size, compressedBlob.size)
              console.debug(
                `[ImageUpload] Compression complete: ${formatFileSize(file.size)} -> ${formatFileSize(compressedBlob.size)} (${stats.savingsPercent}% smaller, format: ${compressedBlob.type})`,
              )
            }
          } else if (debug) {
            console.debug(
              `[ImageUpload] Compression skipped: compressed result (${formatFileSize(compressedBlob.size)}) was not smaller than original (${formatFileSize(file.size)})`,
            )
          }
          
          setCompressionStatus(null)
        } catch (compressionError) {
          console.warn('[ImageUpload] Compression failed, using original file:', compressionError)
          setCompressionStatus(null)
          // Continue with original file if compression fails
        }
      }

      // Step 2: Upload to Blossom
      setIsLoading(true)
      const result = await uploadFileToBlossom(fileToUpload, {
        preferredServer: preferredServer || selectedServer,
        onProgress: (progress) => {
          const pct = Math.round((progress.loaded / progress.total) * 100)
          if (debug) {
            console.debug(`[ImageUpload] Upload progress: ${pct}%`)
          }
        },
        onError: (error, serverUrl) => {
          console.error(`[ImageUpload] Upload error on ${serverUrl}:`, error)
        },
        maxRetries: 3,
        debug,
      })

      // Update the input value with the uploaded URL
      setInputValue(result.url)

      // Keep input editable so URL is visible and can be edited
      setInputEditable(false)

      // Save the uploaded image - this will trigger parent to update src prop
      // which will then update localSrc through the useEffect
      onSave({ url: result.url, index })

      toast.success('Image uploaded successfully')
    } catch (err: any) {
      console.error('[ImageUpload] Upload error:', err)
      toast.error(err.message || 'Upload failed')
      throw err
    } finally {
      setIsLoading(false)
      setCompressionStatus(null)
    }
  }

  useEffect(() => {
    setLocalSrc(src)
    // Also update inputValue to display the URL when image exists
    // Use src if no initialUrl is provided
    if (src && !initialUrl) {
      setInputValue(src)
    } else if (initialUrl) {
      setInputValue(initialUrl)
    }
  }, [src, initialUrl])

  const handleUploadIntent = async () => {
    if (!hasInteracted && onInteraction) {
      setHasInteracted(true)
      onInteraction()
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*,video/*'
    input.multiple = !forSingle
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      if (files.length) {
        try {
          await performBlossomUpload(files[0])
        } catch (err) {
          console.error('Blossom upload error', err)
          toast.error('Upload failed. Try again.')
        }
      }
    }
    input.click()
  }

  function handleDragEnter(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault()
    setIsDragging(true)

    if (!hasInteracted && onInteraction) {
      setHasInteracted(true)
      onInteraction()
    }
  }

  function handleDragLeave(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault()
    setIsDragging(false)
  }

  function handleDrop(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault()
    setIsDragging(false)

    if (!hasInteracted && onInteraction) {
      setHasInteracted(true)
      onInteraction()
    }

    const files = Array.from(e.dataTransfer?.files || [])
    if (files.length) {
      ;(async () => {
        try {
          await performBlossomUpload(files[0])
        } catch (err) {
          console.error('Blossom upload error', err)
          toast.error('Upload failed. Try again.')
        }
      })()
    }
  }

  const handleEditByUpload = async () => {
    if (!hasInteracted && onInteraction) {
      setHasInteracted(true)
      onInteraction()
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      if (files.length) {
        try {
          await performBlossomUpload(files[0])
        } catch (err) {
          console.error('Blossom upload error', err)
          toast.error('Upload failed. Try again.')
        }
      }
    }
    input.click()
  }

  function handleInput(event: React.ChangeEvent<HTMLInputElement>): void {
    if (!hasInteracted && onInteraction) {
      setHasInteracted(true)
      onInteraction()
    }

    if (inputTimeoutRef.current) {
      clearTimeout(inputTimeoutRef.current)
    }

    const newValue = event.target.value
    setInputValue(newValue)

    if (onUrlChange) {
      onUrlChange(newValue)
    }

    inputTimeoutRef.current = setTimeout(() => {
      if (!newValue.trim()) {
        setUrlError(null)
        return
      }
      try {
        new URL(newValue)
        setUrlError(null)
      } catch {
        setUrlError('Invalid URL format')
      }
    }, 300)
  }

  function handleInputFocus() {
    if (!hasInteracted && onInteraction) {
      setHasInteracted(true)
      onInteraction()
    }
  }

  function handleSaveImage() {
    if (!inputValue) return
    if (urlError) return

    onSave({ url: inputValue, index })

    if (index === -1) {
      setInputValue('')
    }

    if (inputEditable) {
      setInputEditable(false)
    }
  }

  function handleCompactUploadIntent() {
    if (onUploadAttempt && !onUploadAttempt()) return
    compactInputRef.current?.click()
  }

  if (compact) {
    return (
      <div className="flex gap-2">
        <Input
          value={inputValue}
          type="url"
          className="border-2"
          placeholder="https://example.com/image.png"
          onChange={handleInput}
          onFocus={handleInputFocus}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={isLoading}
          onClick={handleCompactUploadIntent}
          aria-label={isLoading ? 'Uploading image' : 'Upload image to Blossom'}
          title={isLoading ? 'Uploading image' : 'Upload image to Blossom'}
        >
          {isLoading ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <span className="w-4 h-4 i-upload" />}
        </Button>
        <input
          ref={compactInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void performBlossomUpload(file)
            event.target.value = ''
          }}
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full">
      <div className="flex flex-col">
        <div
          className={`border-2 border-b-0 border-black relative w-full aspect-video overflow-hidden
            ${localSrc ? 'bg-black' : ''}`}
          style={localSrc ? {} : { backgroundImage: 'url("images/checker.png")', backgroundRepeat: 'repeat' }}
        >
          {/* Floating server selector */}
          <div className="top-2 left-2 z-10 absolute">
            <Select value={selectedServer} onValueChange={setSelectedServer}>
              <SelectTrigger className="bg-white shadow-sm border-2 border-black w-[280px]">
                <SelectValue placeholder="Select server" />
              </SelectTrigger>
              <SelectContent>
                {BLOSSOM_SERVERS.map((server) => (
                  <SelectItem key={server.url} value={server.url}>
                    {server.name} ({server.plan})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {localSrc ? (
            <>
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-300 to-white" style={{ clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}></div>
                <div className="absolute inset-0 bg-gradient-to-l from-gray-300 to-white" style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}></div>
              </div>

              <div className="absolute inset-0 flex justify-center items-center" style={{ backgroundImage: 'url("images/image-bg-pattern.png")', backgroundRepeat: 'repeat' }}>
                {getMediaType(localSrc) === 'video' ? (
                  <video src={localSrc} controls className="max-w-full max-h-full object-contain">
                    <track kind="captions" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img src={localSrc} alt="uploaded media" className="max-w-full max-h-full object-contain" />
                )}
              </div>

              <div className="right-2 bottom-2 absolute flex gap-2">
                {inputEditable && (
                  <Button type="button" variant="outline" size="icon" className="bg-white" onClick={handleEditByUpload}>
                    <span className="w-6 h-6 i-upload" />
                  </Button>
                )}
                <Button type="button" variant="outline" size="icon" className="bg-white" onClick={() => onDelete(index)}>
                  <span className="w-4 h-4 i-delete" />
                </Button>
              </div>

              {index !== -1 && (
                <div className="bottom-2 left-2 absolute flex flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="bg-white"
                    disabled={index === 0}
                    onClick={() => onPromote && onPromote(index)}
                  >
                    <span className="w-4 h-4 i-up" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="bg-white"
                    disabled={index === imagesLength - 1}
                    onClick={() => onDemote && onDemote(index)}
                  >
                    <span className="w-4 h-4 i-down" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <button
              type="button"
              className={`absolute inset-0 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-black/5 ${
                isDragging ? 'bg-black/10' : ''
              }`}
              onClick={handleUploadIntent}
              onDragEnter={handleDragEnter}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <span className="w-10 h-10 i-upload" />
              <strong>{isDragging ? 'Drop media here' : 'Click or drag image here'}</strong>
              <strong className="text-gray-500 text-xs">{imageDimensionText}</strong>
            </button>
          )}
        </div>

        {/* URL input below image - full width */}
        <div className="relative w-full">
          <Input
            disabled={!inputEditable && Boolean(localSrc)}
            value={inputValue}
            type="text"
            className="pr-12 border-2 border-black rounded-none h-12"
            placeholder="Set a remote image URL"
            id="userImageRemote"
            name="imageRemoteInput"
            onChange={handleInput}
            onFocus={handleInputFocus}
            data-testid="image-url-input"
          />
          {localSrc ? (
            inputEditable ? (
              <Button
                type="button"
                className="top-1 right-1 bottom-1 absolute h-10"
                onClick={handleSaveImage}
                data-testid="image-save-button"
              >
                Save
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="top-1 right-1 bottom-1 absolute bg-white h-10"
                onClick={() => setInputEditable(true)}
                data-testid="image-edit-button"
              >
                Edit
              </Button>
            )
          ) : (
            <Button
              type="button"
              className="top-1 right-1 bottom-1 absolute h-10"
              onClick={handleSaveImage}
              data-testid="image-save-button"
            >
              Save
            </Button>
          )}
        </div>

        {urlError && (
          <p className="text-destructive text-sm">{urlError}</p>
        )}

        {isLoading && (
          <div className="flex flex-col gap-2">
            {compressionStatus && (
              <div className="flex flex-row items-center gap-2">
                <div className="border-2 border-primary border-t-transparent rounded-full w-4 h-4 animate-spin"></div>
                <p className="text-sm">{compressionStatus}</p>
              </div>
            )}
            {!compressionStatus && (
              <div className="flex flex-row items-center gap-2">
                <div className="border-2 border-primary border-t-transparent rounded-full w-4 h-4 animate-spin"></div>
                <p className="text-sm">Uploading...</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
